import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { add_to_card, add_to_wishlist } from '../../store/reducers/cardReducer';
import { imgUrl } from '../../utils/imgUrl';
import { DISPLAY, formatQ, priceLabel, Heart, Eye, Plus } from '../home/ui';

const BADGE = {
    new: 'bg-[#16a34a] text-white',
    discount: 'bg-[#f87171] text-white',
    top: 'bg-[#1e3a8a] text-white',
    order: 'bg-[#fbbf24] text-[#1e3a8a]',
    out: 'bg-[#64748b] text-white',
};

const Stars = ({ rating = 0, count = 0 }) => {
    const full = Math.round(Number(rating) || 0);
    return (
        <span className='inline-flex items-center gap-1 text-[11.5px] text-[#64748b] font-semibold'>
            <span className='text-[#fbbf24] tracking-[1px]'>{'★'.repeat(full)}{'☆'.repeat(5 - full)}</span>
            ({count})
        </span>
    );
};

/*
 * Tarjeta de producto del diseño v1-marketplace.
 * `tags`: subconjunto de ['new', 'top'] que decide la sección que la pinta.
 * Carrito y favoritos usan los mismos thunks de siempre; los toasts los
 * muestra la página (una sola vez), no cada tarjeta.
 * `horizontal`: vista de lista del listado (foto a la izquierda).
 */
const ProductCard = ({ product: p, tags = [], horizontal = false }) => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { userInfo } = useSelector(state => state.auth);

    const add_card = () => {
        if (!userInfo) { navigate('/login'); return; }
        dispatch(add_to_card({ userId: userInfo.id, quantity: 1, productId: p._id }));
    };

    const add_wishlist = () => {
        if (!userInfo) { navigate('/login'); return; }
        dispatch(add_to_wishlist({
            userId: userInfo.id,
            productId: p._id,
            name: p.name,
            price: p.price,
            image: imgUrl(p.images?.[0] || p.image),
            discount: p.discount,
            rating: p.rating,
            slug: p.slug,
        }));
    };

    const badges = [];
    if (tags.includes('new')) badges.push(['new', 'Nuevo']);
    if (p.discount > 0) badges.push(['discount', `-${p.discount}%`]);
    if (tags.includes('top')) badges.push(['top', 'Top venta']);
    if (p.bajo_pedido) badges.push(['order', 'Bajo pedido']);
    else if (p.stock !== undefined && p.stock <= 0) badges.push(['out', 'Agotado']);

    const oldPrice = p.discount > 0 ? p.price * 100 / (100 - p.discount) : null;
    const detail = `/product/details/${p.slug}`;
    const img = imgUrl(p.images?.[0] || p.image);

    return (
        <article className={`group bg-white rounded-[18px] border-[1.5px] border-[#f1efea] p-3 flex h-full relative overflow-hidden transition-all duration-200 hover:-translate-y-1 hover:border-transparent hover:shadow-[0_6px_18px_-6px_rgba(15,23,42,.08)] ${horizontal ? 'flex-row items-center gap-5 sm:gap-3' : 'flex-col gap-[10px]'}`}>
            <div className={`relative rounded-[14px] overflow-hidden bg-[#faf9f6] ${horizontal ? 'w-[170px] sm:w-[110px] flex-shrink-0' : ''}`} style={{ aspectRatio: '1 / 1' }}>
                <Link to={detail} className='absolute inset-0'>
                    {img
                        ? <img src={img} alt={p.name} loading='lazy' className='w-full h-full object-cover' />
                        : <span className='w-full h-full grid place-items-center text-[44px] opacity-30'>🐾</span>}
                </Link>

                {badges.length > 0 && (
                    <div className='absolute top-2 left-2 flex flex-col items-start gap-[5px] z-[2] pointer-events-none'>
                        {badges.map(([kind, label]) => (
                            <span key={kind} className={`text-[10.5px] font-extrabold px-[9px] py-[4px] rounded-full uppercase tracking-[.04em] ${BADGE[kind]}`}>{label}</span>
                        ))}
                    </div>
                )}

                <div className='absolute top-2 right-2 flex flex-col gap-[5px] z-[2] translate-x-[50px] opacity-0 group-hover:translate-x-0 group-hover:opacity-100 group-focus-within:translate-x-0 group-focus-within:opacity-100 transition-all duration-300'>
                    <button onClick={add_wishlist} aria-label='Agregar a favoritos'
                        className='w-[34px] h-[34px] rounded-full bg-white text-[#1e3a8a] grid place-items-center shadow-[0_2px_6px_rgba(15,23,42,.12)] hover:bg-[#0891b2] hover:text-white hover:scale-[1.08] transition-all'>
                        <Heart className='w-[15px] h-[15px]' />
                    </button>
                    <Link to={detail} aria-label='Ver producto'
                        className='w-[34px] h-[34px] rounded-full bg-white text-[#1e3a8a] grid place-items-center shadow-[0_2px_6px_rgba(15,23,42,.12)] hover:bg-[#0891b2] hover:text-white hover:scale-[1.08] transition-all'>
                        <Eye className='w-[15px] h-[15px]' />
                    </Link>
                </div>
            </div>

            <div className={`flex flex-col flex-1 min-w-0 ${horizontal ? 'gap-[6px]' : 'gap-[10px]'}`}>
                {(p.category || p.brand) && (
                    <span className='text-[10.5px] text-[#0891b2] font-extrabold tracking-[.06em] uppercase leading-none truncate'>
                        {[p.category, p.brand].filter(Boolean).join(' · ')}
                    </span>
                )}
                <Link to={detail}>
                    <h4 className='text-[14px] font-bold text-[#0f172a] leading-[1.3] m-0 hover:text-[#0891b2] transition-colors'
                        style={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden', minHeight: horizontal ? undefined : '36px' }}>
                        {p.name}
                    </h4>
                </Link>
                <Stars rating={p.rating} count={p.reviewCount || 0} />

                <div className='flex items-baseline gap-2 flex-wrap mt-auto'>
                    <span className={`${p.price > 0 ? 'text-[19px]' : 'text-[15px]'} font-extrabold text-[#1e3a8a] tracking-[-.01em]`} style={DISPLAY}>{priceLabel(p.price)}</span>
                    {oldPrice && <span className='text-[13px] text-[#64748b] line-through'>{formatQ(oldPrice)}</span>}
                </div>

                <button onClick={add_card}
                    className={`mt-[6px] bg-[#faf9f6] text-[#1e3a8a] font-bold text-[13px] py-[10px] rounded-[11px] inline-flex items-center justify-center gap-[6px] border-[1.5px] border-[#f1efea] hover:bg-[#1e3a8a] hover:text-white hover:border-[#1e3a8a] transition-all ${horizontal ? 'self-start px-[22px]' : 'w-full px-3'}`}>
                    <Plus className='w-[14px] h-[14px]' />
                    Agregar
                </button>
            </div>
        </article>
    );
};

export default ProductCard;
